import type {
  ProfessionalCardRequestCompleteness,
  ProfessionalCardRequestEditBody,
  ProfessionalCardRequestSchema,
} from "@asap/shared/schemas/professional-card-request";
import type { z } from "zod";
import type { schemaProfessionalCardSecondaryRequest } from "~/zod/schema/professionalCardRequest.schema";
import { getProfessionalCardRefusalReasons } from "@asap/shared/utils/professional-card-refusal";
import type { ContractStatus, ProfessionalCardRefusalReason, RequestStatus } from "@asap/shared/utils/supabase.enum";
import { ContractStatusSchema } from "@asap/shared/types/supabase.types.gen";
import type { Color } from "@asap/design/styles/colors";

const PROFESSIONAL_CARD_REQUEST_COMPLETENESS_BADGE: Record<
  ProfessionalCardRequestCompleteness,
  { color: "success" | "warning"; icon: string }
> = {
  complete: {
    color: "success",
    icon: "i-ph-check-circle",
  },
  missing_photo: {
    color: "warning",
    icon: "i-ph-camera-slash",
  },
};

export const getProfessionalCardRequestCompletenessBadge = (completeness: ProfessionalCardRequestCompleteness) =>
  PROFESSIONAL_CARD_REQUEST_COMPLETENESS_BADGE[completeness];

// The mockup's colours come with `professional-card-request-details-v2`.
export const getProfessionalCardRequestStatusColor = (status: RequestStatus, isDetailsV2Enabled: boolean): Color => {
  switch (status) {
    case "pending":
      return isDetailsV2Enabled ? "warning" : "info";
    case "approved":
      return "success";
    case "refused":
      return isDetailsV2Enabled ? "critical" : "warning";
    case "canceled":
      return "gray";
  }
};

// A pending request only: a treated one goes back to pending first. The API tells what it lacks otherwise.
export const isProfessionalCardRequestApprovable = ({
  status,
  approval_blocker,
}: Pick<ProfessionalCardRequestSchema, "status" | "approval_blocker">): boolean =>
  status === "pending" && approval_blocker === null;

export const canApproveProfessionalCardRequest = ({
  status,
  completeness,
}: Pick<ProfessionalCardRequestSchema, "status" | "completeness">): boolean =>
  status === "pending" && completeness === "complete";

// A treated request goes back to pending for a second look, except a refused one: its consultant resubmits it
// once corrected.
export const canMarkProfessionalCardRequestAsPending = ({
  status,
}: Pick<ProfessionalCardRequestSchema, "status">): boolean => status === "approved" || status === "canceled";

// The API refuses a request that is no longer pending too.
export const canRefuseProfessionalCardRequest = ({ status }: Pick<ProfessionalCardRequestSchema, "status">): boolean =>
  status === "pending";

// The provisional card treats a pending request; a treated one already has its outcome.
export const canAddProfessionalCardToRequest = ({ status }: Pick<ProfessionalCardRequestSchema, "status">): boolean =>
  status === "pending";

// The reasons of the request's type, less the photo one while the talent has no photo to reject.
export const getProfessionalCardRefusalReasonOptions = ({
  is_secondary_request,
  completeness,
}: Pick<ProfessionalCardRequestSchema, "is_secondary_request" | "completeness">): ProfessionalCardRefusalReason[] =>
  getProfessionalCardRefusalReasons(is_secondary_request === true).filter(
    (reason) => reason !== "non_compliant_photo" || completeness === "complete"
  );

// The comment details the refusal: it is all "Autre" says, and it goes with no other reason.
export const isProfessionalCardRefusalCommentRequired = (reason: ProfessionalCardRefusalReason | undefined): boolean =>
  reason === "other";

// Rejecting the photo keeps the request pending and asks the talent for a new one: the WSM is told before confirming.
export const isProfessionalCardPhotoRejection = (reason: ProfessionalCardRefusalReason | undefined): boolean =>
  reason === "non_compliant_photo";

// A refused request reopens through its consultant, once corrected: only they edit and resubmit it.
export const canResubmitProfessionalCardRequest = (
  { status, owner_id }: Pick<ProfessionalCardRequestSchema, "status" | "owner_id">,
  teamMemberId: string | undefined
): boolean => status === "refused" && owner_id === teamMemberId;

export const canEditProfessionalCardRequest = (
  request: Pick<ProfessionalCardRequestSchema, "status" | "owner_id">,
  teamMemberId: string | undefined
): boolean => request.status === "pending" || canResubmitProfessionalCardRequest(request, teamMemberId);

type ProfessionalCardRefusalDetails = {
  refusedAt: string;
  refusedBy: ProfessionalCardRequestSchema["refused_by_team_member"];
  comment: string | null;
};

// The colour says who the request waits on: the talent (warning) or the consultant (critical), who corrects it.
export type ProfessionalCardRefusalBanner = (
  | { kind: "refused" }
  | ({ kind: "refusedFor"; reason: ProfessionalCardRefusalReason } & ProfessionalCardRefusalDetails)
  | ({ kind: "photoRejected" } & ProfessionalCardRefusalDetails)
) & { color: "warning" | "critical" };

// A request refused before reasons were recorded only says it was refused. The WSM who refused may have left since.
// A rejected photo leaves the request pending until the talent sends a new one.
export const getProfessionalCardRefusalBanner = ({
  status,
  completeness,
  refusal_reason,
  refusal_comment,
  refused_at,
  refused_by_team_member,
}: Pick<
  ProfessionalCardRequestSchema,
  "status" | "completeness" | "refusal_reason" | "refusal_comment" | "refused_at" | "refused_by_team_member"
>): ProfessionalCardRefusalBanner | null => {
  const isPhotoAwaited = status === "pending" && completeness === "missing_photo";
  if (isPhotoAwaited && refusal_reason === "non_compliant_photo" && refused_at) {
    return {
      kind: "photoRejected",
      color: "warning",
      refusedAt: refused_at,
      refusedBy: refused_by_team_member,
      comment: refusal_comment,
    };
  }
  if (status !== "refused") return null;
  if (!refusal_reason || !refused_at) return { kind: "refused", color: "critical" };
  return {
    kind: "refusedFor",
    color: "critical",
    reason: refusal_reason,
    refusedAt: refused_at,
    refusedBy: refused_by_team_member,
    comment: refusal_comment,
  };
};

// A carte BTP request takes any of the talent's contracts but the cancelled ones.
export const PROFESSIONAL_CARD_REQUEST_CONTRACT_STATUSES: ContractStatus[] = ContractStatusSchema.options
  .map((status) => status.value)
  .filter((status) => status !== "cancelled");

// The select lists every eligible contract of the talent: no talent has that many.
export const PROFESSIONAL_CARD_REQUEST_CONTRACTS_PAGE_SIZE = 100;

// The mission fields of the form, as the request edit takes them: the server keeps the status and the ownership.
export const toProfessionalCardMissionEdit = (
  parsedRequest: z.infer<typeof schemaProfessionalCardSecondaryRequest>
): ProfessionalCardRequestEditBody => ({
  agency_id: parsedRequest.agency_id,
  construction_site_address: parsedRequest.construction_site_address,
  construction_site_address_details: parsedRequest.construction_site_address_details,
  location: parsedRequest.location,
  start_date: parsedRequest.start_date,
  end_date: parsedRequest.end_date,
});
